import React, { useState } from "react";
import PropTypes from "prop-types";
// [I2 FIX] Use shared constant instead of hardcoded array
import { MODEL_FAMILIES } from "../../utils/constants";

const EMPTY_PACKAGE = {
  modelFamily: MODEL_FAMILIES[0].key,
  creditAmount: "",
  priceCents: "",
  name: "",
  stripePriceId: "",
  sortOrder: 0,
};

/**
 * Admin section for managing purchasable credit packages.
 * Table with create/edit/deactivate functionality.
 */
const CreditPackagesSection = ({
  packages,
  onCreate,
  onUpdate,
  onDelete,
  onReactivate,
}) => {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_PACKAGE);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  const resetForm = () => {
    setFormData(EMPTY_PACKAGE);
    setEditingId(null);
    setShowForm(false);
    setFormError("");
  };

  const startCreate = () => {
    setFormData(EMPTY_PACKAGE);
    setEditingId(null);
    setShowForm(true);
    setFormError("");
  };

  const startEdit = (pkg) => {
    setFormData({
      modelFamily: pkg.modelFamily,
      creditAmount: pkg.creditAmount,
      priceCents: pkg.priceCents,
      name: pkg.name || "",
      stripePriceId: pkg.stripePriceId || "",
      sortOrder: pkg.sortOrder || 0,
    });
    setEditingId(pkg.id);
    setShowForm(true);
    setFormError("");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value === "" ? "" : Number(value),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    const creditAmount = Number(formData.creditAmount);
    const priceCents = Number(formData.priceCents);

    if (!creditAmount || creditAmount < 1) {
      setFormError("Credit amount must be at least 1");
      return;
    }
    if (!priceCents || priceCents < 1) {
      setFormError("Price must be at least $0.01");
      return;
    }

    const payload = {
      modelFamily: formData.modelFamily,
      creditAmount,
      priceCents,
      name: formData.name.trim() || undefined,
      stripePriceId: formData.stripePriceId.trim() || undefined,
      sortOrder: Number(formData.sortOrder) || 0,
    };

    setSubmitting(true);
    try {
      if (editingId) {
        await onUpdate(editingId, payload);
      } else {
        await onCreate(payload);
      }
      resetForm();
    } catch (err) {
      // Parent handlers toast and rethrow — this sets inline form error
      setFormError(err.response?.data?.message || "Failed to save package");
    } finally {
      setSubmitting(false);
    }
  };

  // price_cents is in CENTS (integer), unlike plans.price which is DOLLARS
  const formatPrice = (cents) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  return (
    <div className="admin-credits-section">
      <div className="admin-credits-section-header">
        <h3>Credit Packages</h3>
        {!showForm && (
          <button className="admin-btn admin-btn-primary" onClick={startCreate}>
            + Add Package
          </button>
        )}
      </div>
      <p className="admin-form-hint admin-section-hint">
        Purchasable credit bundles. Users buy these via Stripe checkout.
        Deactivating hides a package from purchase but preserves the record.
      </p>

      {showForm && (
        <form className="admin-package-form" onSubmit={handleSubmit}>
          <div className="admin-form-group">
            <label htmlFor="pkg-family">Family</label>
            <select
              id="pkg-family"
              name="modelFamily"
              value={formData.modelFamily}
              onChange={handleChange}
            >
              {MODEL_FAMILIES.map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label}
                </option>
              ))}
            </select>
          </div>

          <div className="admin-form-group">
            <label htmlFor="pkg-amount">Credits</label>
            <input
              id="pkg-amount"
              name="creditAmount"
              type="number"
              min="1"
              value={formData.creditAmount}
              onChange={handleNumberChange}
              placeholder="100"
              required
            />
          </div>

          <div className="admin-form-group">
            <label htmlFor="pkg-price">Price (cents)</label>
            <input
              id="pkg-price"
              name="priceCents"
              type="number"
              min="1"
              value={formData.priceCents}
              onChange={handleNumberChange}
              placeholder="499"
              required
            />
          </div>

          <div className="admin-form-group">
            <label htmlFor="pkg-name">Name</label>
            <input
              id="pkg-name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g. Starter Pack"
            />
          </div>

          {/* [U1 FIX] stripePriceId and sortOrder were in data model but missing from UI */}
          <div className="admin-form-group">
            <label htmlFor="pkg-stripe-id">Stripe Price ID</label>
            <input
              id="pkg-stripe-id"
              name="stripePriceId"
              type="text"
              value={formData.stripePriceId}
              onChange={handleChange}
              placeholder="price_..."
            />
          </div>

          <div className="admin-form-group">
            <label htmlFor="pkg-sort-order">Sort Order</label>
            <input
              id="pkg-sort-order"
              name="sortOrder"
              type="number"
              min="0"
              value={formData.sortOrder}
              onChange={handleNumberChange}
            />
          </div>

          <button
            type="submit"
            className="admin-btn admin-btn-primary"
            disabled={submitting}
          >
            {submitting ? "..." : editingId ? "Update" : "Create"}
          </button>
          <button
            type="button"
            className="admin-btn admin-btn-cancel"
            onClick={resetForm}
          >
            Cancel
          </button>

          {formError && (
            <div className="admin-form-error admin-form-error-full">
              {formError}
            </div>
          )}
        </form>
      )}

      <div className="admin-table-wrapper">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Family</th>
              <th>Credits</th>
              <th>Price</th>
              <th>Stripe Price ID</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {packages.length === 0 ? (
              <tr>
                <td colSpan={7} className="admin-empty-row">
                  No credit packages configured yet.
                </td>
              </tr>
            ) : (
              packages.map((pkg) => (
                <tr
                  key={pkg.id}
                  className={pkg.isActive ? "" : "admin-row-inactive"}
                >
                  <td>{pkg.name || "—"}</td>
                  <td className="admin-family-name">{pkg.modelFamily}</td>
                  <td className="admin-limits-cell">
                    {pkg.creditAmount.toLocaleString()}
                  </td>
                  <td>{formatPrice(pkg.priceCents)}</td>
                  <td>
                    <code className="admin-code-small">
                      {pkg.stripePriceId || "—"}
                    </code>
                  </td>
                  <td>
                    <span
                      className={`admin-invite-status ${pkg.isActive ? "admin-invite-status-accepted" : "admin-invite-status-expired"}`}
                    >
                      {pkg.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="admin-actions-cell">
                    <button
                      className="admin-btn admin-btn-edit"
                      onClick={() => startEdit(pkg)}
                    >
                      Edit
                    </button>
                    {pkg.isActive ? (
                      <button
                        className="admin-btn admin-btn-delete"
                        onClick={() => onDelete(pkg)}
                      >
                        Deactivate
                      </button>
                    ) : (
                      /* [U4 FIX] Reactivation goes through parent confirm, same as deactivation */
                      <button
                        className="admin-btn admin-btn-primary"
                        onClick={() => onReactivate(pkg)}
                      >
                        Reactivate
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// [C3 FIX] Proper shape matching backend response
CreditPackagesSection.propTypes = {
  packages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      modelFamily: PropTypes.string.isRequired,
      creditAmount: PropTypes.number.isRequired,
      priceCents: PropTypes.number.isRequired,
      name: PropTypes.string,
      stripePriceId: PropTypes.string,
      isActive: PropTypes.bool.isRequired,
      sortOrder: PropTypes.number,
    }),
  ).isRequired,
  onCreate: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onReactivate: PropTypes.func.isRequired,
};

export default CreditPackagesSection;
